import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { sessionApi, getAuthSession } from '../services/api';
import '../styles/SessionPayment.css';

function SessionPaymentPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });

  useEffect(() => {
    loadSessionDetails();
    loadUserData();
    loadRazorpayScript();
  }, [sessionId]);

  const loadSessionDetails = async () => {
    try {
      const response = await sessionApi.getById(sessionId);
      setSession(response);
      setLoading(false);
    } catch (err) {
      setError('Failed to load session details');
      setLoading(false);
    }
  };

  const loadUserData = () => {
    try {
      const sessionData = getAuthSession();
      const user = sessionData?.user;
      if (user) {
        const nameParts = (user.name || '').split(' ');
        setFormData(prev => ({
          ...prev,
          firstName: user.firstName || nameParts[0] || '',
          lastName: user.lastName || nameParts.slice(1).join(' ') || '',
          email: user.email || '',
          phone: user.phone || ''
        }));
      }
    } catch (err) {
      console.error('Error loading user data:', err);
    }
  };

  const loadRazorpayScript = () => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRazorpayPayment = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.email || !formData.phone) {
      setError('Please fill in all required fields');
      return;
    }

    if (session.price <= 0) {
      // Free session - just book without payment
      await handleBookSession();
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Create Razorpay order
      const orderResponse = await fetch('/api/payments/session/razorpay/order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthSession()?.token || ''}`
        },
        body: JSON.stringify({
          sessionId: sessionId,
          amount: session.price * 100,
          currency: 'INR',
          customerEmail: formData.email,
          customerPhone: formData.phone,
          customerName: `${formData.firstName} ${formData.lastName}`,
          customerAddress: formData.address
        })
      });

      if (!orderResponse.ok) {
        throw new Error('Failed to create payment order');
      }

      const orderData = await orderResponse.json();
      const orderId = orderData.orderId || orderData.id;

      if (!window.Razorpay) {
        throw new Error('Razorpay not loaded');
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY || 'YOUR_RAZORPAY_KEY',
        amount: session.price * 100,
        currency: 'INR',
        order_id: orderId,
        name: 'Mental Health Platform',
        description: `Booking: ${session.title}`,
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.phone
        },
        handler: async (response) => {
          try {
            // Verify payment
            const verifyResponse = await fetch('/api/payments/session/razorpay/verify', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getAuthSession()?.token || ''}`
              },
              body: JSON.stringify({
                sessionId: sessionId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            if (verifyResponse.ok) {
              const bookingData = await verifyResponse.json();
              // Redirect to receipt page
              navigate(`/session-payment-receipt/${bookingData.bookingId}`, {
                state: {
                  bookingId: bookingData.bookingId,
                  paymentId: response.razorpay_payment_id,
                  orderId: orderId
                }
              });
            } else {
              setError('Payment verification failed');
              setProcessing(false);
            }
          } catch (err) {
            setError('Payment verification error: ' + err.message);
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
            setError('Payment cancelled');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError('Payment error: ' + err.message);
      setProcessing(false);
    }
  };

  const handleBookSession = async () => {
    try {
      const bookingData = await sessionApi.bookSession(sessionId);
      navigate(`/session-payment-receipt/${bookingData._id}`, {
        state: {
          bookingId: bookingData._id,
          isFreeSession: true
        }
      });
    } catch (err) {
      setError('Failed to book session: ' + err.message);
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="session-payment-container">
        <div className="spinner">Loading session details...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="session-payment-container">
        <div className="error-message">Session not found</div>
      </div>
    );
  }

  return (
    <div className="session-payment-container">
      <div className="session-payment-wrapper">
        <button className="close-btn" onClick={() => navigate(-1)}>✕</button>

        <div className="payment-header">
          <h1>Complete Your Booking</h1>
          <p>Book the session and secure your spot</p>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="payment-content">
          {/* Session Summary */}
          <div className="session-summary">
            <h3>Session Summary</h3>
            <div className="summary-item">
              <span>Session Title:</span>
              <strong>{session.title}</strong>
            </div>
            <div className="summary-item">
              <span>Therapist:</span>
              <strong>{session.therapistName || 'Dr. Anonymous'}</strong>
            </div>
            <div className="summary-item">
              <span>Topic:</span>
              <strong>{session.topic}</strong>
            </div>
            <div className="summary-item">
              <span>Mode:</span>
              <strong className={`badge badge-${session.mode}`}>
                {session.mode === 'online' ? '🌐 Online' : '📍 Offline'}
              </strong>
            </div>
            <div className="summary-item">
              <span>Date & Time:</span>
              <strong>{new Date(session.scheduledAt).toLocaleString()}</strong>
            </div>
            <div className="summary-item">
              <span>Duration:</span>
              <strong>{session.durationHours} hours</strong>
            </div>
            <div className="summary-item">
              <span>Available Spots:</span>
              <strong>{session.availableSlots} / {session.maxParticipants}</strong>
            </div>
            
            {/* Price Section */}
            <div className="price-section">
              {session.price > 0 ? (
                <>
                  <div className="summary-item">
                    <span>Session Price:</span>
                    <strong>₹{session.price}</strong>
                  </div>
                  <div className="summary-item">
                    <span>Tax (0%):</span>
                    <strong>₹0</strong>
                  </div>
                  <div className="summary-item total">
                    <span>Total Amount:</span>
                    <strong>₹{session.price}</strong>
                  </div>
                </>
              ) : (
                <div className="summary-item free-badge">
                  <span>✨ FREE SESSION</span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Form */}
          <div className="payment-form">
            <h3>Your Details</h3>
            <form onSubmit={handleRazorpayPayment}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="First Name"
                    disabled={processing}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Last Name"
                    disabled={processing}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email Address"
                    disabled={processing}
                  />
                </div>
                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Phone Number"
                    disabled={processing}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Street Address"
                  disabled={processing}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    disabled={processing}
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State"
                    disabled={processing}
                  />
                </div>
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    placeholder="Zip Code"
                    disabled={processing}
                  />
                </div>
              </div>

              {/* Payment Method Selection */}
              {session.price > 0 && (
                <div className="payment-methods">
                  <h4>Payment Method</h4>
                  <label className="radio-group">
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="razorpay"
                      checked={paymentMethod === 'razorpay'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      disabled={processing}
                    />
                    <span className="radio-label">
                      <strong>💳 Razorpay</strong> - Credit Card, Debit Card, UPI, Net Banking
                    </span>
                  </label>
                </div>
              )}

              <button
                type="submit"
                className="btn-payment"
                disabled={processing}
              >
                {processing ? (
                  <>
                    <span className="spinner-small"></span> Processing...
                  </>
                ) : session.price > 0 ? (
                  `💳 Pay ₹${session.price} & Book`
                ) : (
                  '✨ Complete Free Booking'
                )}
              </button>
            </form>

            <div className="security-info">
              🔒 Your payment is 100% secure with Razorpay
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SessionPaymentPage;
